import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import api from "../services/api";
import { API_ENDPOINTS } from "../constants/API_ENDPOINTS";

interface User {
  id: string;
  token: string;
  refresh_token: string;
  userId: string;
  username: string;
  role: string;
  email: string;
  whatsapp_number: string | null;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  register: (
    username: string,
    email: string,
    password: string,
    confirmPassword: string,
    whatsapp_number?: string,
    require_email_verification?: boolean,
    require_whatsapp_verification?: boolean,
    captcha?: { captcha_id: string; captcha_answer: string }
  ) => Promise<any>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshToken = useCallback(async () => {
    try {
      const userStr = localStorage.getItem("user");
      if (!userStr) return;

      const userData = JSON.parse(userStr);
      const response = await api.post(API_ENDPOINTS.REFRESH_TOKEN, {
        refresh_token: userData.refresh_token,
      });

      if (response.data && response.data.access_token) {
        const newUserData = {
          ...userData,
          token: response.data.access_token,
        };
        localStorage.setItem("user", JSON.stringify(newUserData));
        setUser(newUserData);
        api.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${response.data.access_token}`;
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      // Don't logout on refresh error, just log it
    }
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (user) {
      // Refresh token every 6 days (since token expires in 7 days)
      const refreshInterval = setInterval(
        refreshToken,
        6 * 24 * 60 * 60 * 1000
      );
      return () => clearInterval(refreshInterval);
    }
  }, [user, refreshToken]);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const userData = JSON.parse(userStr);
          // Verify the token is still valid
          const response = await api.get(API_ENDPOINTS.VERIFY_TOKEN);
          if (response.data.authenticated) {
            setUser(userData);
          } else {
            localStorage.removeItem("user");
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        localStorage.removeItem("user");
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    if (user) {
      api.defaults.headers.common["Authorization"] = `Bearer ${user.token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, [user]);

  const login = async (username: string, password: string) => {
    try {
      console.log("Attempting login for user:", username);
      const response = await api.post(API_ENDPOINTS.LOGIN, {
        username,
        password,
      });
      console.log("Login response:", response.data);
      if (response.data && response.data.access_token) {
        const userData: User = {
          id: response.data.user_id,
          token: response.data.access_token,
          refresh_token: response.data.refresh_token,
          userId: response.data.user_id,
          username: response.data.username,
          role: response.data.role || "user",
          email: response.data.email || "",
          whatsapp_number: response.data.whatsapp_number || null,
        };
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        localStorage.setItem("token", response.data.access_token);
      } else {
        throw new Error("Invalid response format from server");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  };

  const register = async (
    username: string,
    email: string,
    password: string,
    confirmPassword: string,
    whatsapp_number?: string,
    require_email_verification: boolean = true,
    require_whatsapp_verification: boolean = false,
    captcha?: { captcha_id: string; captcha_answer: string }
  ) => {
    try {
      if (password !== confirmPassword) {
        throw new Error("Passwords do not match");
      }
      const response = await api.post(API_ENDPOINTS.REGISTER, {
        username,
        email,
        password,
        whatsapp_number: whatsapp_number ? whatsapp_number : null,
        require_email_verification,
        require_whatsapp_verification,
        website: "", // honeypot — must stay empty
        captcha_id: captcha?.captcha_id,
        captcha_answer: captcha?.captcha_answer,
      });
      if (response.data) {
        const userData: User = {
          id: response.data.user_id,
          token: response.data.access_token,
          refresh_token: response.data.refresh_token,
          userId: response.data.user_id,
          username: response.data.username,
          role: response.data.role || "user",
          email: response.data.email,
          whatsapp_number: response.data.whatsapp_number || null,
        };
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);

        // Return the full response for verification handling
        return response.data;
      } else {
        throw new Error("Invalid response format from server");
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
    delete api.defaults.headers.common["Authorization"];
    setError(null);
  };

  const value = { user, loading, error, login, register, logout };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
